import { create } from 'zustand';

export interface TerminalTabConfig {
  id: string;
  title: string;
  /** 终端类型 */
  type?: 'container' | 'kvm';
  /** KVM 终端：WebSocket URL */
  url?: string;
  urlNeedToken?: boolean;
}

export type TerminalWindowMode = 'normal' | 'maximized' | 'minimized';

function getInitialLayout() {
  if (typeof window === 'undefined') {
    return { position: { x: 0, y: 0 }, size: { width: 900, height: 400 } };
  }
  const height = Math.max(Math.floor(window.innerHeight / 3), 400);
  const width = window.innerWidth;
  const x = 0;
  const y = window.innerHeight - height;
  return {
    position: { x, y },
    size: { width, height },
  };
}

interface TerminalWindowState {
  visible: boolean;
  tabs: TerminalTabConfig[];
  activeTabId: string | null;
  position: { x: number; y: number };
  size: { width: number; height: number };
  windowMode: TerminalWindowMode;
}

export const useTerminalWindowStore = create<
  TerminalWindowState & {
    addTab: (config: Omit<TerminalTabConfig, 'id'>) => string;
    removeTab: (id: string) => void;
    setActiveTab: (id: string) => void;
    closeWindow: () => void;
    openTab: (config: Omit<TerminalTabConfig, 'id'>) => string;
    updatePosition: (pos: { x: number; y: number }) => void;
    updateSize: (size: { width: number; height: number }) => void;
    setWindowMode: (mode: TerminalWindowMode) => void;
    restoreToInitial: () => void;
  }
>((set, get) => ({
  visible: false,
  tabs: [],
  activeTabId: null,
  position: { x: 0, y: 0 },
  size: { width: 900, height: 400 },
  windowMode: 'normal',

  addTab: (config) => {
    const id = `terminal-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
    const tab: TerminalTabConfig = { ...config, id };
    set((state) => {
      const isInitialOpen = state.tabs.length === 0;
      const { position, size } = isInitialOpen
        ? getInitialLayout()
        : { position: state.position, size: state.size };
      return {
        visible: true,
        tabs: [...state.tabs, tab],
        activeTabId: id,
        position,
        size,
        windowMode: 'normal', // 新增标签时始终显示窗口
      };
    });
    return id;
  },

  removeTab: (id) => {
    set((state) => {
      const newTabs = state.tabs.filter((t) => t.id !== id);
      const newActiveId = state.activeTabId === id ? (newTabs[0]?.id ?? null) : state.activeTabId;
      return {
        tabs: newTabs,
        activeTabId: newActiveId,
        visible: newTabs.length > 0,
      };
    });
  },

  setActiveTab: (id) => set({ activeTabId: id }),

  closeWindow: () => set({ visible: false, tabs: [], activeTabId: null }),

  openTab: (config) => {
    const id = get().addTab(config);
    return id;
  },

  updatePosition: (position) => set({ position }),

  updateSize: (size) => set({ size }),

  setWindowMode: (windowMode) => set({ windowMode }),

  restoreToInitial: () => {
    const { position, size } = getInitialLayout();
    set({ position, size, windowMode: 'normal' });
  },
}));
